// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Center, Code, Group, Loader, Paper, Select, Stack, Table, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { formatHumanName, normalizeErrorString } from '@medplum/core';
import type { Organization, Practitioner, ProjectMembership, User } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { POLARIS_ENV_TAG_SYSTEM } from '@polarissleep/shared';
import { IconDeviceFloppy, IconRefresh } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';

/**
 * Standalone admin page (direct URL only, no sidebar entry) for inspecting
 * which Organization each Practitioner's ProjectMembership compartment is
 * scoped to, and switching it.
 *
 * The compartment scope is the `organization` parameter on the membership's
 * `access[].parameter[]` array. Medplum AccessPolicies template `%organization`
 * placeholders against that parameter at request time — so updating it
 * changes which tenant compartment the user can see. The Practitioner's own
 * `meta.account` is not modified here; this only touches ProjectMembership.
 *
 * Reachable at /admin/user-org-scope.
 */

const ORG_PARAM_NAME = 'organization';

interface MembershipRow {
  membership: ProjectMembership;
  practitioner?: Practitioner;
  user?: User;
  currentOrgId?: string;
}

export function UserOrgScope(): JSX.Element {
  const medplum = useMedplum();
  const [rows, setRows] = useState<MembershipRow[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const memberships = await medplum.searchResources('ProjectMembership', { _count: '200' });
      const practitionerMemberships = memberships.filter((m) => m.profile?.reference?.startsWith('Practitioner/'));

      const practitioners = await Promise.all(
        practitionerMemberships.map((m) =>
          m.profile?.reference
            ? medplum.readReference<Practitioner>({ reference: m.profile.reference }).catch(() => undefined)
            : Promise.resolve(undefined)
        )
      );

      const users = await Promise.all(
        practitionerMemberships.map((m) =>
          m.user?.reference
            ? medplum.readReference<User>({ reference: m.user.reference }).catch(() => undefined)
            : Promise.resolve(undefined)
        )
      );

      const orgList = await medplum.searchResources('Organization', { _count: '200', _sort: 'name' });

      const newRows: MembershipRow[] = practitionerMemberships.map((m, i) => ({
        membership: m,
        practitioner: practitioners[i],
        user: users[i],
        currentOrgId: extractOrgIdFromMembership(m),
      }));

      // Sort: rows with no current org first (they're the ones that need attention),
      // then alphabetical by name.
      newRows.sort((a, b) => {
        if (!a.currentOrgId && b.currentOrgId) {
          return -1;
        }
        if (a.currentOrgId && !b.currentOrgId) {
          return 1;
        }
        return displayName(a).localeCompare(displayName(b));
      });

      setRows(newRows);
      setOrgs(orgList);
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const orgOptions = useMemo(
    () => orgs.filter((o) => o.id).map((o) => ({ value: o.id as string, label: orgLabel(o) })),
    [orgs]
  );

  const orgsById = useMemo(() => {
    const m: Record<string, Organization> = {};
    for (const o of orgs) {
      if (o.id) {
        m[o.id] = o;
      }
    }
    return m;
  }, [orgs]);

  const handleSave = useCallback(
    async (row: MembershipRow) => {
      const membershipId = row.membership.id;
      if (!membershipId) {
        return;
      }
      const newOrgId = pending[membershipId];
      if (!newOrgId || newOrgId === row.currentOrgId) {
        return;
      }
      setSavingId(membershipId);
      try {
        // Re-read the current membership to avoid version conflict.
        const fresh = await medplum.readResource('ProjectMembership', membershipId);
        const updated = applyOrgToMembership(fresh, newOrgId);
        const saved = await medplum.updateResource(updated);
        const savedOrgId = extractOrgIdFromMembership(saved);
        if (savedOrgId !== newOrgId) {
          throw new Error(
            `Save succeeded but verification failed: server-side org parameter is "${savedOrgId ?? 'none'}", expected "${newOrgId}".`
          );
        }
        showNotification({
          color: 'green',
          message: `${displayName(row)} now scoped to ${orgLabel(orgsById[newOrgId] ?? ({ id: newOrgId } as Organization))}`,
        });
        await load();
        setPending((p) => {
          const c = { ...p };
          delete c[membershipId];
          return c;
        });
      } catch (err) {
        showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
      } finally {
        setSavingId(undefined);
      }
    },
    [medplum, pending, load, orgsById]
  );

  return (
    <Stack gap="md" p="md">
      <PageHeader
        title="User Organization Scope"
        actions={
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="default"
            onClick={() => {
              load().catch(console.error);
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <Paper withBorder radius="md" shadow="sm" p="md">
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            Every Practitioner ProjectMembership in this project. The "Current org" column reads the{' '}
            <Code>organization</Code> parameter from <Code>access[].parameter[]</Code> on the membership. Picking a new
            Organization and saving rewrites that parameter (other parameters and the AccessPolicy reference are
            preserved). After save, the row is re-read from the server to verify the change actually landed.
          </Text>

          {loading && (
            <Center py="xl">
              <Loader />
            </Center>
          )}
          {!loading && rows.length === 0 && (
            <Text c="dimmed" size="sm">
              No Practitioner memberships found.
            </Text>
          )}
          {!loading && rows.length > 0 && (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Practitioner</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Current org</Table.Th>
                  <Table.Th>Switch to</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const id = row.membership.id ?? '';
                  const pendingChoice = pending[id];
                  const isDirty = !!pendingChoice && pendingChoice !== row.currentOrgId;
                  const currentOrg = row.currentOrgId ? orgsById[row.currentOrgId] : undefined;
                  return (
                    <Table.Tr key={id}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Group gap="xs">
                            <Text size="sm" fw={500}>
                              {displayName(row)}
                            </Text>
                            {row.membership.admin && (
                              <Badge color="grape" size="xs" variant="light">
                                admin
                              </Badge>
                            )}
                          </Group>
                          <Code style={{ fontSize: 10 }}>{id}</Code>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.user?.email ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {row.currentOrgId ? (
                          <Stack gap={2}>
                            <Text size="sm">{currentOrg ? orgLabel(currentOrg) : '(not in project)'}</Text>
                            <Code style={{ fontSize: 10 }}>{row.currentOrgId}</Code>
                          </Stack>
                        ) : (
                          <Badge color="gray" variant="light" size="sm">
                            none
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          placeholder="Pick Organization"
                          data={orgOptions}
                          value={pendingChoice ?? null}
                          onChange={(v) => {
                            if (!v) {
                              return;
                            }
                            setPending((p) => ({ ...p, [id]: v }));
                          }}
                          searchable
                          clearable
                          w={320}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          leftSection={<IconDeviceFloppy size={14} />}
                          onClick={() => {
                            handleSave(row).catch(console.error);
                          }}
                          loading={savingId === id}
                          disabled={!isDirty}
                        >
                          Save
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

function extractOrgIdFromMembership(m: ProjectMembership): string | undefined {
  for (const a of m.access ?? []) {
    for (const p of a.parameter ?? []) {
      if (p.name === ORG_PARAM_NAME) {
        const ref = p.valueReference?.reference;
        if (ref?.startsWith('Organization/')) {
          return ref.slice('Organization/'.length);
        }
      }
    }
  }
  return undefined;
}

function applyOrgToMembership(m: ProjectMembership, newOrgId: string): ProjectMembership {
  const newRef = { reference: `Organization/${newOrgId}` };
  const access = m.access ?? [];
  if (access.length === 0) {
    throw new Error(
      'Membership has no access[] entry, so there is nothing to attach the organization parameter to. Project admins typically have no access policy; switch the org for non-admin memberships instead, or add an AccessPolicy to this membership first.'
    );
  }
  const newAccess = access.map((a) => {
    const params = (a.parameter ?? []).filter((p) => p.name !== ORG_PARAM_NAME);
    params.push({ name: ORG_PARAM_NAME, valueReference: newRef });
    return { ...a, parameter: params };
  });
  return { ...m, access: newAccess };
}

function displayName(row: MembershipRow): string {
  if (row.practitioner?.name?.[0]) {
    return formatHumanName(row.practitioner.name[0]);
  }
  return row.membership.profile?.display ?? row.membership.profile?.reference ?? '(unknown)';
}

function orgLabel(o: Organization): string {
  const envTag = o.meta?.tag?.find((t) => t.system === POLARIS_ENV_TAG_SYSTEM);
  const base = o.name ?? o.id ?? '(unnamed)';
  return envTag?.code ? `${base} [${envTag.code}]` : base;
}
